import React from 'react'
import { Feather } from '../../../components/icon/vectorIcons'
// mi6 ships web only, so a real anchor is available here. It matters: an <a>
// gives middle-click, cmd-click and the browser's own "open in new tab" for
// free, none of which window.open can offer. DraggableList and FileDropzone
// take the same escape hatch.
// eslint-disable-next-line no-restricted-imports
import styledWeb from 'styled-components'

import useTranslation from '../../../hooks/useTranslation'
import { IContentFeedBoost } from '../queries/getContentFeeds'

const ICON_SIZE = 20

/** Matches the Feather cog on these panels and the trash icon's fill. */
const ICON_COLOR = '#21201B'

/**
 * Where a content type is edited in mi6, mirroring the MediaEdit and
 * DocumentEdit routes in LinkingConfiguration. Keep the two in step -- these are
 * plain strings, so a route rename will not break the build, only the link.
 *
 * Doclets return null deliberately: mi6 has no doclet screen, so there is
 * nothing to open.
 */
export const mi6PathForItem = (
  itemType: string,
  itemId: string
): string | null => {
  if (itemType === 'media') return `/media/${itemId}/edit`
  if (itemType === 'document') return `/library/${itemId}/edit`
  return null
}

const iconBox = `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
`

const Anchor = styledWeb.a`
  ${iconBox}
  text-decoration: none;
`

/**
 * Holds the control's space when there is nothing to link to, rather than
 * omitting it. The action column is right-aligned, so dropping the icon on
 * doclet rows would shuffle the trash icon sideways row by row.
 */
const Inert = styledWeb.span`
  ${iconBox}
  opacity: 0.35;
`

interface OpenInMi6LinkProps {
  /** Undefined for an empty hero slot. */
  boost?: IContentFeedBoost
}

/**
 * Opens the item's own mi6 page in a new tab so a curator can confirm they
 * picked the right thing, and fix it, from the feed listing.
 *
 * The new tab is load-bearing rather than a preference: these pages hold feed
 * edits as an unpublished draft in local state, so navigating away in place
 * would throw them out.
 */
export const OpenInMi6Link = ({ boost }: OpenInMi6LinkProps) => {
  const { t } = useTranslation()

  // A boost with no item outlived a deleted record: the id still maps to a
  // route, but there would be nothing there to verify.
  const path =
    boost && boost.item ? mi6PathForItem(boost.itemType, boost.itemId) : null

  if (!path) {
    return (
      <Inert title={t('contentCuration:detail:openItemUnavailable')}>
        <Feather name="external-link" size={ICON_SIZE} color={ICON_COLOR} />
      </Inert>
    )
  }

  return (
    <Anchor
      href={path}
      target="_blank"
      // noopener is the one that earns its place: the item page must not get a
      // window handle back onto a tab holding unpublished feed edits.
      rel="noopener noreferrer"
      title={t('contentCuration:detail:openItem')}
      aria-label={t('contentCuration:detail:openItem')}
    >
      <Feather name="external-link" size={ICON_SIZE} color={ICON_COLOR} />
    </Anchor>
  )
}

export default OpenInMi6Link
